import React, {useCallback, useMemo} from 'react';
import {Menu} from 'antd';
import PropTypes from 'prop-types';
import {useHistory, useLocation} from 'react-router-dom';
import {useUser} from '@dofleini/security';
import {useTranslation} from 'react-i18next';
import {AREA_TYPE_IDS} from '@/modules/clientRegisters/contants/clientRegisters';
import {useVerifyLabDataAccess} from '@/hooks/useVerifyLabDataAccess';

const {Item, SubMenu} = Menu;

export function renderMenu(
  { menu,  title,  link,  action, ...item },
  index,
  props = {},
  pathname = '',
  defaultSelectedKeys = '',
  scrollRef = null,
  isFromSidebar = false,
  onNavigate = null,
) {
  const key = link ? `link=>${link}` : `action=>${action || index}`;

  // eslint-disable-next-line react/prop-types
  const disabled = props?.disabled;

  if (item.divider)
    return <Menu.Divider key={`${key}=>divider=>${index}`}/>;

  if (menu)
    return (
      <SubMenu disabled={disabled}
        // key={`${index}-${title}`}
        key={link}
        title={title}
        {...item}
        onTitleClick={({domEvent}) => {
          scrollRef?.scrollTop && scrollRef.scrollTop(scrollRef.getScrollHeight() + domEvent?.target?.clientHeight * menu?.length);
          scrollRef?.handleScroll();
          if (link === '/client-registers' || link === '/lab-data') onNavigate && onNavigate(link);
        }}
        className={`sub-menu-sidebar${(link === '/client-registers' || link === '/lab-data') && pathname === link ? ' sidebar-menu-item-active' : ''}`}
        popupClassName={isFromSidebar ? 'sub-menu-collapsed-popover popover-left-78' : 'sub-menu-collapsed-popover'}
      >
        {menu.map((_item, _index) => renderMenu(_item, _index, props, pathname, defaultSelectedKeys, scrollRef, isFromSidebar, onNavigate))}
      </SubMenu>
    );

  return (
    <Item
      disabled={disabled || item?.disabled}
      key={key} {...item} {...props}
      className={
        item?.disabled
          ? 'sidebar-menu-item-disabled'
          : pathname === link || link === defaultSelectedKeys || (pathname?.includes(link))
            ? 'sidebar-menu-item-active'
            : ''
      }
    >
      {title}
    </Item>
  );
}

const NavMenu = ({mode, menu, theme, onClick, menuProps, subMenuProps, selectable, scrollRef, collapsed, isFromSidebar}) => {
  const { user } = useUser();
  const {push} = useHistory();
  const {pathname} = useLocation();
  // eslint-disable-next-line no-unused-vars
  const {t} = useTranslation('navigation');

  const {
    isLabDataFood,
    isLabDataPool,
    isLabDataLegionella,
    isLabDataOthers,
    hasNotLabDataRestriction
  } = useVerifyLabDataAccess();

  const hasNotRegisterRestriction = useMemo(() => {
    return (!user?.registersAccess || (user.registersAccess?.length === 0));
  }, [user.registersAccess]);

  const isRegisterFood = useMemo(() => {
    return user?.registersAccess?.some((item) => item.areaAccess === AREA_TYPE_IDS.KITCHEN);
  }, [user?.registersAccess]);

  const isRegisterPool = useMemo(() => {
    return user?.registersAccess?.some((item) => item.areaAccess === AREA_TYPE_IDS.POOL);
  }, [user?.registersAccess]);

  const isRegisterLegionella = useMemo(() => {
    return user?.registersAccess?.some((item) => item.areaAccess === AREA_TYPE_IDS.LEGIONELLA);
  }, [user?.registersAccess]);

  const isRegisterOthers = useMemo(() => {
    return user?.registersAccess?.some((item) => item.areaAccess === AREA_TYPE_IDS.OTHERS);
  }, [user?.registersAccess]);

  const filteredMenuClientAuditor = useMemo(() => {
    if (!menu.some((item) => item.isClient || item.isAuditor)) return menu;
    if (user?.isAuditor)
      return menu.filter((item) => item.isAuditor === true);
    if (user?.isAuditor === false)
      return menu.filter((item) => item.isClient === true);
    return menu;
  }, [menu, user?.isAuditor]);

  const filteredMenuLabData = useMemo(() => {
    let menu = filteredMenuClientAuditor;
    if (hasNotLabDataRestriction) return menu;
    const labIndex = menu.findIndex((item) => item.link === '/lab-data');
    if (!isLabDataFood && labIndex > -1) {
      const foodIndex = menu?.[labIndex]?.menu?.findIndex((item) => item.link === '/lab-data/food');
      if (foodIndex > -1) menu?.[labIndex]?.menu?.splice(foodIndex, 1);
    }
    if (!isLabDataPool && labIndex > -1) {
      const poolIndex = menu?.[labIndex]?.menu?.findIndex((item) => item.link === '/lab-data/pool');
      if (poolIndex > -1) menu?.[labIndex]?.menu?.splice(poolIndex, 1);
    }
    if (!isLabDataLegionella && labIndex > -1) {
      const legionellaIndex = menu?.[labIndex]?.menu?.findIndex((item) => item.link === '/lab-data/legionella');
      if (legionellaIndex > -1) menu?.[labIndex]?.menu?.splice(legionellaIndex, 1);
    }
    if (!isLabDataOthers && labIndex > -1) {
      const othersIndex = menu?.[labIndex]?.menu?.findIndex((item) => item.link === '/lab-data/others');
      if (othersIndex > -1) menu?.[labIndex]?.menu?.splice(othersIndex, 1);
    }
    return menu;
  }, [filteredMenuClientAuditor, hasNotLabDataRestriction, isLabDataFood, isLabDataLegionella, isLabDataOthers, isLabDataPool]);

  const filteredMenu = useMemo(() => {
    let menu = filteredMenuLabData;
    if (hasNotRegisterRestriction) return menu;
    const registerIndex = menu.findIndex((item) => item.link === '/client-registers');
    if (!isRegisterFood && registerIndex > -1) {
      const foodIndex = menu?.[registerIndex]?.menu?.findIndex((item) => item.link === '/client-registers/kitchen');
      if (foodIndex > -1) menu?.[registerIndex]?.menu?.splice(foodIndex, 1);
    }
    if (!isRegisterPool && registerIndex > -1) {
      const poolIndex = menu?.[registerIndex]?.menu?.findIndex((item) => item.link === '/client-registers/pool');
      if (poolIndex > -1) menu?.[registerIndex]?.menu?.splice(poolIndex, 1);
    }
    if (!isRegisterLegionella && registerIndex > -1) {
      const legionellaIndex = menu?.[registerIndex]?.menu?.findIndex((item) => item.link === '/client-registers/legionella');
      if (legionellaIndex > -1) menu?.[registerIndex]?.menu?.splice(legionellaIndex, 1);
    }
    if (!isRegisterOthers && registerIndex > -1) {
      const othersIndex = menu?.[registerIndex]?.menu?.findIndex((item) => item.link === '/client-registers/others');
      if (othersIndex > -1) menu?.[registerIndex]?.menu?.splice(othersIndex, 1);
    }
    return menu;
  }, [filteredMenuLabData, hasNotRegisterRestriction, isRegisterFood, isRegisterLegionella, isRegisterOthers, isRegisterPool]);

  const handleClick = useCallback(({item, key, domEvent, ...e}) => {
    if (!item.props?.elementRef?.current?.className?.includes('sidebar-menu-item-disabled')) {
      scrollRef?.scrollTop && scrollRef?.scrollTop(domEvent.screenY);
      const [eventType, value] = key.split('=>');
      e[eventType] = value;
      onClick({item, key, domEvent, ...e});
    }
  }, [onClick, scrollRef]);

  const defaultSelectedKeys = useMemo(() => {
    let selected = '';
    menu?.map(item => item?.menu?.map(subItem => pathname?.includes(subItem?.link) ? selected = subItem.link : null));
    return selected;
  }, [menu, pathname]);

  const defaultOpenKeys = useMemo(() => {
    const currentSubMenu = menu?.find(x => x.menu?.some(i => i?.link === defaultSelectedKeys))?.link;
    return [currentSubMenu];
  }, [defaultSelectedKeys, menu]);

  return (
    <Menu
      mode={mode}
      theme={theme}
      onClick={handleClick}
      selectable={selectable || false}
      {...menuProps}
      className="global-sidebar-menu"
      defaultOpenKeys={collapsed ? [] : defaultOpenKeys}
    >
      {filteredMenu.map((item, index) => renderMenu(item, index, subMenuProps, pathname, defaultSelectedKeys, scrollRef, isFromSidebar, push))}
    </Menu>
  );
};

export default NavMenu;

NavMenu.propTypes = {
  menu: PropTypes.array,
  mode: PropTypes.string,
  onClick: PropTypes.func,
  theme: PropTypes.string,
  subMenuProps: PropTypes.object,
  menuProps: PropTypes.object,
  selectable: PropTypes.bool,
  scrollRef: PropTypes.object,
  collapsed: PropTypes.bool,
  isFromSidebar: PropTypes.bool,
};

NavMenu.defaultProps = {
  menu: [],
  mode: 'inline',
  subMenuProps: {},
  menuProps: {},
  selectable: true
};

renderMenu.propTypes = {
  link: PropTypes.string,
  menu: PropTypes.array,
  title: PropTypes.string,
  action: PropTypes.string,
};
